/**
 * The card pictures a parent has already uploaded.
 *
 * Nothing used to ask the backend what was on file, so returning to the screen showed two empty
 * boxes and the form demanded the card again. Parents did the only thing they could — upload it
 * again — and one family in dev ended up with 55 rows for 4 pictures. These turn what the
 * backend stores into the two slots the screen draws: front and back.
 */

export const emptyCardSlots = () => [null, null];

const slotFromFileName = (objectPath) => {
    const name = String(objectPath || '').split('/').pop().toLowerCase();

    // Medicare files are named …-Front.jpg / …-Back.jpg; Health Care Card files …-HCCPicture-0/1.
    if (name.includes('front')) return 0;
    if (name.includes('back')) return 1;
    if (/-1\.[^.]*$/.test(name)) return 1;
    if (/-0\.[^.]*$/.test(name)) return 0;
    return null;
};

/** Card numbers arrive as 1234567890-1 or 1234567890#1; expiries as 05/2028. */
const canonical = (value) => String(value ?? '').replace(/[^0-9a-z]/gi, '').toUpperCase();

/**
 * The same day, however it was stored.
 *
 * A Health Care Card expiry is saved as "30-11-2029", sent with the pictures as "2029-11-30",
 * and often read back as "2029-11-30T00:00:00". Punctuation-stripping leaves those as different
 * strings, so the pictures of the card just uploaded no longer match the date on screen.
 * Month/year values such as "05/2028" are left as they are.
 */
const expiryKey = (value) => {
    const raw = String(value ?? '').trim();
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (iso) return `${iso[1]}${iso[2]}${iso[3]}`;

    const dayMonthYear = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(raw);
    if (dayMonthYear) {
        return `${dayMonthYear[3]}${dayMonthYear[2].padStart(2, '0')}${dayMonthYear[1].padStart(2, '0')}`;
    }

    return canonical(raw);
};

const sameCard = (a = {}, b = {}) =>
    canonical(a.number) === canonical(b.number) && expiryKey(a.expiry) === expiryKey(b.expiry);

/**
 * Whether a stored picture is evidence of the card now being claimed.
 *
 * A picture proves one card. If the parent has since changed the number or the expiry, the old
 * picture proves nothing about the new card and they have to supply it again.
 *
 * A picture with no card recorded against it — uploaded before we started recording that, or
 * imported from the previous system — is accepted only for the card the school already holds. It
 * cannot vouch for a change nobody has seen.
 */
const provesCard = (file, card) => {
    if (!card?.current) return true;

    const recorded = { number: file.cardNumber, expiry: file.cardExpiry };
    if (recorded.number || recorded.expiry) return sameCard(recorded, card.current);

    return !card.original || sameCard(card.current, card.original);
};

/**
 * Maps what the backend returned onto [front, back].
 *
 * Only files it could sign are usable: the rest are paths left by the previous system, which are
 * not in cloud storage and cannot be shown. Those must not fill a slot, or the parent would be
 * told a card is on file that nobody can see.
 *
 * Pass `card` as { current, original } to keep only the pictures that prove the current card.
 */
export const slotsFromStoredFiles = (files = [], card) => {
    const slots = emptyCardSlots();

    (files || [])
        .filter((file) => file && file.signedUrl && provesCard(file, card))
        .forEach((file) => {
            const named = slotFromFileName(file.objectPath);
            const index = named !== null ? named : slots.findIndex((slot) => !slot);
            if (index >= 0 && index < slots.length && !slots[index]) {
                slots[index] = file.signedUrl;
            }
        });

    return slots;
};

/** A side counts as supplied whether it was just chosen or was already on file. */
export const cardSideMissing = (chosenFiles = [], storedSlots = []) =>
    emptyCardSlots().some((_, index) => !chosenFiles[index] && !storedSlots[index]);

/** Something new to send: at least one side chosen now, and no side left empty. */
export const readyToUpload = (chosenFiles = [], storedSlots = []) =>
    emptyCardSlots().some((_, index) => Boolean(chosenFiles[index]))
    && !cardSideMissing(chosenFiles, storedSlots);
